import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Users, ShieldCheck, Leaf, Smile, Check, Home, SquareDashedBottom, Building, Brush, Droplet, Sun, Shield } from 'lucide-react';
import ReusableContactForm from '../../components/ReusableContactForm/ReusableContactForm';
import styles from './IronRailings.module.css';

const IronRailings = () => {
    return (
        <div className={styles.container}>
            <Helmet>
                <title>Custom Iron Railings Edmonton | Handcrafted Wrought Iron | ABC Railing</title>
                <meta name="description" content="Premium custom iron railings in Edmonton. Handcrafted by expert artisans, weather-resistant powder coating, Alberta Building Code compliant. Free estimates!" />
                <meta name="keywords" content="iron railings edmonton, wrought iron railings, custom iron railings, handcrafted railings, Alberta Building Code compliant" />
            </Helmet>

            <section className={styles.heroSection}>
                <div className={styles.heroContent}>
                    <h1>Custom Iron Railings Edmonton</h1>
                    <p>Handcrafted iron railings that combine timeless elegance with Edmonton's harsh weather demands. Built by artisans who care about your home as much as you do.</p>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <a href="#consultation">
                            <span>Get Free Estimate</span>
                        </a>
                        <a href="/architectural" className={styles.secondaryCta}>
                            View Our Portfolio
                        </a>
                    </div>
                </div>
            </section>

            <section className={`${styles.whoWeAreSection} ${styles.featuresSection}`}>
                <div className={styles.sectionContent}>
                    <h2>Why Edmonton Chooses ABC Iron Railings</h2>
                    <div className={styles.featuresGrid}>
                        <div className={styles.featureItem}>
                            <div className={styles.iconContainer}>
                                <Leaf className={styles.featureIcon} />
                            </div>
                            <h3>Sustainable Craftsmanship</h3>
                            <p>Our iron railings are built to last generations, not years. We source recycled steel when possible and use eco-friendly powder coating processes that contain no harmful solvents. Every railing we craft reduces the need for future replacements, making it the environmentally responsible choice for Edmonton homes.</p>
                        </div>
                        <div className={styles.featureItem}>
                            <div className={styles.iconContainer}>
                                <Users className={styles.featureIcon} />
                            </div>
                            <h3>Master Craftsmen Team</h3>
                            <p>Our hardworking team of certified welders and metalworkers brings extensive experience to every Edmonton project. We're not just installers – we're artisans who take pride in creating ironwork that becomes a cherished part of your home for decades to come.</p>
                        </div>
                        <div className={styles.featureItem}>
                            <div className={styles.iconContainer}>
                                <ShieldCheck className={styles.featureIcon} />
                            </div>
                            <h3>Code Compliance Experts</h3>
                            <p>Every iron railing system we install exceeds Alberta Building Code requirements. Our team stays current with all Alberta building regulations and ensures your investment is protected with fully compliant installations that pass inspection every time.</p>
                        </div>
                        <div className={styles.featureItem}>
                            <div className={styles.iconContainer}>
                                <Smile className={styles.featureIcon} />
                            </div>
                            <h3>Friendly & Approachable</h3>
                            <p>No intimidating sales tactics or confusing technical jargon. We speak plain English, respect your time, and make the entire process enjoyable. From your first call to final installation, you'll work with the same friendly team who genuinely cares about your satisfaction.</p>
                        </div>
                    </div>
                </div>
            </section>

            <div className={styles.sectionDivider} />

            <section className={styles.expertiseSection}>
                <div className={styles.sectionContent}>
                    <h2>Edmonton Iron Railing Expertise</h2>
                    <div>
                        <p>For over two decades, ABC Railing has been Edmonton's premier destination for custom iron railings that marry Old World craftsmanship with modern engineering. Our iron railings aren't just functional safety features – they're architectural statements that enhance your property's value while standing strong against Edmonton's demanding climate.</p>
                        <p>Edmonton's extreme temperature swings, from -40°C winters to +30°C summers, require iron railings engineered specifically for our unique conditions. Our custom powder coating process creates a protective barrier that prevents rust, corrosion, and weather damage, ensuring your investment looks beautiful year after year.</p>
                    </div>
                    <div className={styles.highlightBox}>
                        <h4>What Makes Our Iron Railings Special</h4>
                        <p>Unlike mass-produced railings, every ABC iron railing is individually designed and handcrafted in our Edmonton shop. We use traditional blacksmithing techniques combined with modern welding technology to create railings that are both stunning and structurally superior. Each piece is custom-fitted to your exact specifications, ensuring perfect integration with your home's architecture.</p>
                    </div>
                </div>
            </section>

            <div className={styles.sectionDivider} />

            <section className={styles.checklistSection}>
                <div className={styles.sectionContent}>
                    <h2>Iron Railing Features and Benefits</h2>
                    <div className={styles.checklistGrid}>
                        <div className={styles.checklistItem}>
                            <h3>Weather Resistant</h3>
                            <ul>
                                <li><Check className={styles.checkIcon} /> Proprietary powder coating system for superior protection</li>
                                <li><Check className={styles.checkIcon} /> Withstands Edmonton's harsh winters and extreme temperatures</li>
                                <li><Check className={styles.checkIcon} /> UV-resistant finish prevents fading and deterioration</li>
                                <li><Check className={styles.checkIcon} /> Eco-friendly with no volatile organic compounds (VOCs)</li>
                                <li><Check className={styles.checkIcon} /> Years of maintenance-free beauty and protection</li>
                            </ul>
                        </div>
                        <div className={styles.checklistItem}>
                            <h3>Unlimited Design Options</h3>
                            <ul>
                                <li><Check className={styles.checkIcon} /> Classic Victorian scrollwork and ornate patterns</li>
                                <li><Check className={styles.checkIcon} /> Modern and contemporary geometric designs</li>
                                <li><Check className={styles.checkIcon} /> Custom designs to match any architectural style</li>
                                <li><Check className={styles.checkIcon} /> Perfect harmony with your home's exterior</li>
                                <li><Check className={styles.checkIcon} /> Collaboration with architects and designers</li>
                            </ul>
                        </div>
                        <div className={styles.checklistItem}>
                            <h3>Precision Engineering</h3>
                            <ul>
                                <li><Check className={styles.checkIcon} /> Expert welding meeting Alberta's highest standards</li>
                                <li><Check className={styles.checkIcon} /> Durable connections built for maximum strength</li>
                                <li><Check className={styles.checkIcon} /> High-quality fasteners and anchoring systems</li>
                                <li><Check className={styles.checkIcon} /> Exceeds typical building code requirements</li>
                                <li><Check className={styles.checkIcon} /> Rigorous quality control at every step</li>
                            </ul>
                        </div>
                        <div className={styles.checklistItem}>
                            <h3>Quick Installation</h3>
                            <ul>
                                <li><Check className={styles.checkIcon} /> Precise shop fabrication for perfect fit</li>
                                <li><Check className={styles.checkIcon} /> Most installations completed in 1-2 days</li>
                                <li><Check className={styles.checkIcon} /> Minimal disruption to your daily routine</li>
                                <li><Check className={styles.checkIcon} /> Careful protection of your property</li>
                                <li><Check className={styles.checkIcon} /> Thorough cleanup after installation</li>
                            </ul>
                        </div>
                        <div className={styles.checklistItem}>
                            <h3>Increase Property Value</h3>
                            <ul>
                                <li><Check className={styles.checkIcon} /> Boosts home value by 3-8%</li>
                                <li><Check className={styles.checkIcon} /> Highly desirable feature for homebuyers</li>
                                <li><Check className={styles.checkIcon} /> Commands premium pricing in real estate market</li>
                                <li><Check className={styles.checkIcon} /> Timeless appeal that never goes out of style</li>
                                <li><Check className={styles.checkIcon} /> Excellent return on investment</li>
                            </ul>
                        </div>
                        <div className={styles.checklistItem}>
                            <h3>Quality Guarantee</h3>
                            <ul>
                                <li><Check className={styles.checkIcon} /> Comprehensive warranty coverage</li>
                                <li><Check className={styles.checkIcon} /> Protection for both materials and workmanship</li>
                                <li><Check className={styles.checkIcon} /> No-cost resolution of any issues</li>
                                <li><Check className={styles.checkIcon} /> Long-term peace of mind</li>
                                <li><Check className={styles.checkIcon} /> Commitment to customer satisfaction</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <div className={styles.sectionDivider} />

            <section className={styles.processSection}>
                <div className={styles.sectionContent}>
                    <h2>Our Edmonton Iron Railing Installation Process</h2>
                    <p>From consultation to completion, we ensure your iron railing project exceeds expectations with professional service and superior craftsmanship.</p>

                    <div className={styles.processGrid}>
                        <div className={styles.processItem}>
                            <div className={styles.processNumber}>01</div>
                            <h4>Free Consultation</h4>
                            <p>We visit your Edmonton property for a no-obligation assessment. We'll discuss styles, colors, and budget while taking precise measurements.</p>
                        </div>
                        <div className={styles.processItem}>
                            <div className={styles.processNumber}>02</div>
                            <h4>Custom Design</h4>
                            <p>Our designers create detailed drawings showing exactly how your iron railings will look. We revise until you're 100% satisfied with every detail.</p>
                        </div>
                        <div className={styles.processItem}>
                            <div className={styles.processNumber}>03</div>
                            <h4>Shop Fabrication</h4>
                            <p>Master craftsmen hand-forge your railings in our Edmonton shop using premium materials and time-tested techniques passed down through generations.</p>
                        </div>
                        <div className={styles.processItem}>
                            <div className={styles.processNumber}>04</div>
                            <h4>Quality Inspection</h4>
                            <p>Every weld, joint, and finish detail is inspected to our exacting standards before your railings leave our shop. We guarantee perfection.</p>
                        </div>
                        <div className={styles.processItem}>
                            <div className={styles.processNumber}>05</div>
                            <h4>Expert Installation</h4>
                            <p>Our certified installers ensure code compliance, and complete installation with minimal disruption to your Edmonton property.</p>
                        </div>
                        <div className={styles.processItem}>
                            <div className={styles.processNumber}>06</div>
                            <h4>Final Walkthrough</h4>
                            <p>We conduct a thorough inspection with you, provide care instructions, and ensure you're completely satisfied with your new iron railings.</p>
                        </div>
                    </div>
                </div>
            </section>

            <div className={styles.sectionDivider} />

            <section className={styles.applicationsSection}>
                <div className={styles.sectionContent}>
                    <h2>Iron Railing Applications</h2>
                    <p style={{textAlign: 'center', maxWidth: '800px', margin: '0 auto 2rem'}}>Discover the perfect iron railing solution for every area of your Edmonton property. Our custom designs enhance both safety and style.</p>
                    <div className={styles.applicationsGrid}>
                        <div className={styles.applicationCard}>
                            <h3><Home size={20} /> Residential Staircases</h3>
                            <p>Transform your home's interior with elegant iron stair railings that combine safety with sophisticated design, perfect for both traditional and modern Edmonton homes.</p>
                        </div>
                        <div className={styles.applicationCard}>
                            <h3><SquareDashedBottom size={20} /> Front Entry Railings</h3>
                            <p>Create a stunning first impression with custom iron railings that welcome guests while providing essential safety.</p>
                        </div>
                        <div className={styles.applicationCard}>
                            <h3><Building size={20} /> Commercial Properties</h3>
                            <p>Durable, code-compliant iron railings for businesses, apartment buildings, and public spaces throughout the Edmonton area.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className={styles.testimonialsSection}>
                <div className={styles.sectionContent}>
                    <h2>Edmonton Homeowners Love Our Work</h2>
                    <div className={styles.testimonialsGrid}>
                        <div className={styles.testimonial}>
                            <p>"The iron railing ABC installed transformed our front entrance. The craftsmanship is incredible and it's held up perfectly through three Edmonton winters."</p>
                            <cite>Michael T., Glenora</cite>
                        </div>
                        <div className={styles.testimonial}>
                            <p>"We get compliments on our iron railings all the time. The team was professional, on time, and the quality exceeded our expectations."</p>
                            <cite>Jennifer L., Windermere</cite>
                        </div>
                        <div className={styles.testimonial}>
                            <p>"From design to installation, working with ABC was a pleasure. The iron railings add so much character to our home and the quality is outstanding."</p>
                            <cite>Robert & Sarah K., Strathcona</cite>
                        </div>
                    </div>
                </div>
            </section>

            <section className={styles.specificationsSection}>
                <div className={styles.sectionContent}>
                    <h2>Technical Specifications</h2>
                    <div className={styles.specsGrid}>
                        <div className={styles.specGroup}>
                            <h4>Materials & Construction</h4>
                            <ul>
                                <li>High-quality steel - minimum ASTM A36 grade</li>
                                <li>1-1/4" to 2" Square Posts</li>
                                <li>Custom height and width options</li>
                                <li>Powder coated finish</li>
                            </ul>
                        </div>
                        <div className={styles.specGroup}>
                            <h4>Performance Standards</h4>
                            <ul>
                                <li>Meets or exceeds Alberta Building Code</li>
                                <li>4" maximum spacing between pickets</li>
                                <li>Compliant with structural load requirements for safety and durability</li>
                                <li>Limited 5-year warranty on finish for interior applications</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <div className={styles.sectionDivider} />

            <section className={styles.maintenanceSection}>
                <div className={styles.sectionContent}>
                    <div className={styles.maintenanceContent}>
                        <h2>Iron Railing Maintenance & Care</h2>
                        <p>Keep your iron railings looking their best with these simple maintenance tips from our Edmonton experts.</p>
                        
                        <div className={styles.maintenanceTips}>
                            <div className={styles.tipCard}>
                                <h3><Droplet size={18} /> Regular Cleaning</h3>
                                <p>Interior railings can be dusted with a damp cloth. Exterior railings must be cleaned regularly with mild soap and water to prevent damage from salt, grime, and environmental exposure.</p>
                            </div>
                            <div className={styles.tipCard}>
                                <h3><Sun size={18} /> Seasonal Inspection</h3>
                                <p>For exterior iron products, check for any signs of wear or damage, especially after harsh Edmonton winters. Look for chips in the powder coating or areas that may need touch-ups.</p>
                            </div>
                            <div className={styles.tipCard}>
                                <h3><Shield size={18} /> Protective Measures</h3>
                                <p>Consider applying a fresh coat of car wax to your outdoor railings in spring and fall to enhance the protective powder coating and maintain the finish.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className={styles.sectionDivider} />

            <section className={styles.consultationSection} id="consultation">
                <div className={styles.consultationContent}>
                    <div className={styles.consultationText}>
                        <h2>Transform your vision into reality</h2>
                        <p>Stop dreaming about the perfect iron railings – let's create them together. Your home deserves more than ordinary, and you deserve craftsmen who understand that every curve, every weld, every detail matters.</p>
                        
                        <div className={styles.consultationDetails}>
                            <h4>What happens during your consultation:</h4>
                            <ul>
                                <li><Check size={16} className={styles.checkIcon} /> We listen to YOUR vision (not push our agenda)</li>
                                <li><Check size={16} className={styles.checkIcon} /> Show you designs that match your style and budget</li>
                                <li><Check size={16} className={styles.checkIcon} /> Provide honest advice about what works (and what doesn't)</li>
                                <li><Check size={16} className={styles.checkIcon} /> Give you a detailed quote with ZERO surprises</li>
                            </ul>
                        </div>

                        <div className={styles.serviceAreas}>
                            <p>Serving: Edmonton, Sherwood Park, St. Albert, Spruce Grove, and surrounding areas</p>
                        </div>
                    </div>
                    <div className={styles.contactFormSection}>
                        <ReusableContactForm 
                            title="Book Your FREE Design Consultation"
                            compact={true}
                            className={styles.consultationContactForm}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IronRailings;
